import {
  BaseUserProfile,
  FoodPreference,
  GymEnvironment,
  IInsightConfig,
  KnownMediums,
  ProgramType,
  TimeSpan,
} from 'fitbeat.models';
import { UserWorkoutSession } from '../../entities';
import { DbWorkoutRepository, IUserProfileRepository } from '../../persistence';
import { UserProfileService } from '../user';
import { PseudoRandomGenerator } from '../workoutselection';
import { ContentType } from './ContentType';
import { IContentClient } from './IContentClient';
import { IPost } from './Post';

export interface IInfluencerCards {
  influencerId: string;
  influencerName: string;
}

export interface IInfluencerBooking {
  workoutSession: UserWorkoutSession;
  influencerDetails: {
    fullname: string;
  };
}

export class ContentManager {
  constructor(
    private contentClient: IContentClient,
    private userProfileRepository: IUserProfileRepository,
    private workoutRepository: DbWorkoutRepository,
    private userProfileService: UserProfileService,
  ) {}

  public async getContentFor(contentType: ContentType, now: Date, longTermGoal: ProgramType,
                             foodPreference: FoodPreference, count: number,
                             insightsConfig: IInsightConfig | undefined): Promise<IPost[]> {

    let additionalPriorityPosts: IPost[] = [];

    if (contentType === ContentType.Insights && insightsConfig?.customInsightsPostType != null) {
      additionalPriorityPosts = await this.getCustomContentForOrg(insightsConfig, now);
    }

    const availableContent = await this.contentClient.getAllAvailableContentFor(contentType);
    const filteredContent = this.filter(foodPreference, longTermGoal, availableContent);
    if (contentType === ContentType.Help) {
      return filteredContent;
    }
    const shuffledPosts = this.shuffle(now, filteredContent);
    const combinedPosts = [... additionalPriorityPosts, ...shuffledPosts];
    const limittedPosts = this.limitCount(combinedPosts, count);
    return limittedPosts;
  }

  public async getMarketingContentForUser(uid: string): Promise<IInfluencerCards[]> {
    const userProfile = await this.userProfileRepository.get(uid);
    if (userProfile?.signupSource?.medium !== KnownMediums.influencer ) {
      return [];
    }
    const influencerSource = userProfile.signupSource?.source;
    if (!influencerSource) {
      return [];
    }
    const influencers = await this.userProfileRepository.getUsersByUtmTagSource(
      influencerSource, KnownMediums.influencer);
    return influencers.map(({uid: influencerId, firstname: influencerName}) => {
      return {
        influencerId,
        influencerName,
      };
    });
  }

  public async getInfluencerWorkoutsForUser(uid: string, gymEnvironment: GymEnvironment) {
    const userEnabledFeatures = await this.userProfileService.getEnabledFeaturesForUser(uid, gymEnvironment);
    const userHomeGym = userEnabledFeatures.workout.homeGym;

    const from = TimeSpan.getNowInTimeZone(userHomeGym.timezone);
    const to = TimeSpan.getEndOfDayDateInTwoWeeks(userHomeGym.timezone);
    const influencerWorkoutsInGym = await this.workoutRepository.getAllInfluencerWorkoutsInDateRangeForGym(
      from, to, userHomeGym.name, uid);
    const influencerUserIds = [...new Set(influencerWorkoutsInGym.map(({ userProfileId }) => userProfileId))];
    const influencerUserProfiles = await this.userProfileRepository.getUsersByIds(influencerUserIds);
    return this.addUserDataToWorkoutSession(influencerWorkoutsInGym, influencerUserProfiles);
  }

  private async getCustomContentForOrg(insightsConfig: any, now: Date) {
    const customPostType = insightsConfig.customInsightsPostType;
    let customInsights = await this.contentClient.getAllAvailableContentFor(ContentType.Insights, customPostType);
    customInsights = this.shuffle(now, customInsights);
    const customEvents =  await this.contentClient.getAllAvailableContentFor(ContentType.Event, customPostType);
    return [...customEvents, ...customInsights]; // show events before insights
  }

  private filter(foodPreference: FoodPreference, longTermGoal: ProgramType, availableContent: IPost[]) {
    const foodPreferenceCheck = (content: IPost) => {
      return (content.foodPreferences.indexOf(foodPreference) !== -1 ||
        content.foodPreferences.length === 0);
    };

    const longTermGoalCheck = (content: IPost) => {
      return (content.longTermGoals.indexOf(longTermGoal) !== -1 ||
        content.longTermGoals.length === 0);
    };

    const filteredContent = availableContent.filter(foodPreferenceCheck).filter(longTermGoalCheck);
    return filteredContent;
  }

  private shuffle(now: Date, filteredContent: IPost[]) {
    const shuffleSeed = PseudoRandomGenerator.getSeedFromDate(now);
    const shuffledPosts = PseudoRandomGenerator.shuffleArrayPseudoRandomly(
      filteredContent,
      shuffleSeed,
    );
    return shuffledPosts;
  }

  private limitCount(filteredContent: IPost[], count: number) {
    const limittedPosts = filteredContent.splice(0, count);
    return limittedPosts;
  }

  private addUserDataToWorkoutSession(
    workouts: UserWorkoutSession[],
    profiles: BaseUserProfile[],
  ): IInfluencerBooking[] {
    return workouts.map((workout) => {
      const user = profiles.find((profile) => profile.uid === workout.userProfileId);
      if (!user) {
        throw new Error(
          `Not able to resolve user bio and name. User profile for booking ${workout.uniqueReference} not found`,
        );
      }
      workout.exerciseSessions = [];
      return {
        workoutSession: workout,
        influencerDetails: {
          fullname: user.fullname,
        },
      };
    });
  }
}
